import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RotateCcw, ScanLine, Package, Undo2,
  AlertTriangle, CheckCircle2, History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import apiClient from '@/services/apiClient';

/**
 * Slice D9.4 — Per-section ComboV2 Reversal (Salem-untouched).
 *
 * Three tabs:
 *   • Reverse: scan an assembled combo USN, preview paired components,
 *     enter reason, confirm.
 *   • Loose USNs: pending-wrap component USNs grouped by SKU; select +
 *     wrap into a fresh PENDING_PUTAWAY bundle.
 *   • History: paginated audit list.
 *
 * URL contract (per-section):
 *   GET   /api/per-section/{slug}/combo-v2-reversal/eligible-combos
 *   POST  /api/per-section/{slug}/combo-v2-reversal/preview
 *   POST  /api/per-section/{slug}/combo-v2-reversal/confirm
 *   GET   /api/per-section/{slug}/combo-v2-reversal/history
 *   GET   /api/per-section/{slug}/combo-v2-reversal/loose-usns
 *   POST  /api/per-section/{slug}/combo-v2-reversal/loose-usns/wrap-bundle
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

function ReverseTab({ apiBase }) {
  const [comboInput, setComboInput] = useState('');
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handlePreview = async () => {
    const c = comboInput.trim();
    if (!c) return;
    setBusy(true);
    setPreview(null);
    setLastResult(null);
    try {
      const r = await apiClient.post(`${apiBase}/preview`, { combo_usn: c });
      setPreview(r.data);
      toast.success('Combo loaded — review components below');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load combo');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview || reason.trim().length < 3) {
      toast.error('Reason is required (min 3 chars)');
      return;
    }
    setConfirming(true);
    try {
      const r = await apiClient.post(`${apiBase}/confirm`, {
        combo_usn: preview.combo.combo_usn,
        reason: reason.trim(),
      });
      toast.success(
        `Reversal ${r.data.reversal_id} complete (${r.data.components_queued} pcs queued)`,
      );
      setLastResult(r.data);
      setPreview(null);
      setComboInput('');
      setReason('');
      inputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reverse combo');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <label className="text-sm font-medium">Scan combo USN to reverse</label>
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={comboInput}
            onChange={(e) => setComboInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePreview()}
            placeholder="CMB-COLOR_COMBO-... "
            disabled={busy || confirming}
            className="font-mono"
            data-testid="combo-input"
          />
          <Button
            onClick={handlePreview}
            disabled={busy || confirming || !comboInput.trim()}
            data-testid="preview-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview'}
          </Button>
        </div>
      </Card>

      {lastResult && (
        <Alert
          className="bg-emerald-50 border-emerald-300"
          data-testid="reversal-success-alert"
        >
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertTitle className="text-emerald-800">
            Reversal {lastResult.reversal_id} complete
          </AlertTitle>
          <AlertDescription className="text-emerald-700">
            {lastResult.components_queued} component USN(s) queued for wrap.
            Plan {lastResult.plan_id || ''} re-opened if it had been COMPLETED.
          </AlertDescription>
        </Alert>
      )}

      {preview && (
        <Card className="p-4 space-y-3" data-testid="preview-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-stone-500">Combo USN</div>
              <div className="font-mono font-semibold">
                {preview.combo.combo_usn}
              </div>
            </div>
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-800 border-amber-300"
            >
              {preview.combo.status}
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-stone-600">
            <div><span className="text-stone-400">Design:</span> {preview.combo.design_name || '—'}</div>
            <div><span className="text-stone-400">Shade:</span> {preview.combo.shade || '—'}</div>
            <div><span className="text-stone-400">Size:</span> {preview.combo.size || '—'}</div>
            <div><span className="text-stone-400">Index:</span> #{preview.combo.session_assembled_index}</div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Paired component USNs</div>
            {preview.components.map((c) => (
              <div
                key={c.component_barcode}
                className="border rounded p-2"
                data-testid={`component-${c.component_barcode}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="font-medium">{c.component_name}</span>
                    <span className="ml-2 text-xs font-mono text-stone-500">
                      {c.component_barcode}
                    </span>
                  </div>
                  <Badge>per combo: {c.per_combo_qty}</Badge>
                </div>
                <div className="text-xs space-y-1">
                  {(c.usns || []).map((u) => (
                    <div
                      key={u.usn_code}
                      className="flex items-center justify-between"
                      data-testid={`paired-${u.usn_code}`}
                    >
                      <span className="font-mono">{u.usn_code}</span>
                      <span className="text-stone-500">
                        from {u.bundle_code}
                        {u.already_reversed && (
                          <span className="ml-2 text-rose-700 font-semibold">
                            already reversed
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium">
              Reason (min 3 chars){' '}
              <span className="text-rose-600">*</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Quality issue on shoulder seam"
              data-testid="reason-input"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setPreview(null); setReason(''); }}
              disabled={confirming}
              className="flex-1"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={confirming || reason.trim().length < 3}
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-btn"
            >
              {confirming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Undo2 className="h-4 w-4 mr-1" /> Confirm Reversal
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function LooseTab({ apiBase }) {
  const [byGroup, setByGroup] = useState([]);
  const [rows, setRows] = useState([]);
  const [filterSku, setFilterSku] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bundleCode, setBundleCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterSku ? `?sku_code=${filterSku}` : '';
      const r = await apiClient.get(`${apiBase}/loose-usns${params}`);
      setRows(r.data.loose_usns || []);
      setByGroup(r.data.by_sku || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load loose USNs');
    } finally {
      setLoading(false);
    }
  }, [apiBase, filterSku]);

  useEffect(() => { load(); }, [load]);

  const toggle = (usn) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(usn)) n.delete(usn);
      else n.add(usn);
      return n;
    });
  };

  const handleWrap = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one USN');
      return;
    }
    if (!bundleCode.trim()) {
      toast.error('Enter the new bundle code');
      return;
    }
    setBusy(true);
    try {
      const r = await apiClient.post(`${apiBase}/loose-usns/wrap-bundle`, {
        bundle_code: bundleCode.trim().toUpperCase(),
        usn_codes: Array.from(selected),
      });
      toast.success(
        `Bundle ${r.data.bundle_code} wrapped (${r.data.wrapped_count} USNs) — PENDING PUT-AWAY`,
      );
      setBundleCode('');
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to wrap bundle');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">By SKU</div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="loose-refresh-btn"
          >
            <RotateCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {byGroup.length === 0 ? (
          <div className="text-sm text-stone-500" data-testid="no-loose-empty">
            No loose USNs queued.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {byGroup.map((g) => (
              <Badge
                key={g.sku_code}
                variant={filterSku === g.sku_code ? 'default' : 'outline'}
                className="cursor-pointer px-3 py-2"
                onClick={() => setFilterSku(filterSku === g.sku_code ? '' : g.sku_code)}
                data-testid={`loose-sku-${g.sku_code}`}
              >
                <span className="font-mono mr-1">{g.sku_code}</span>
                <span className="ml-1">{g.pcs} pcs</span>
              </Badge>
            ))}
          </div>
        )}
      </Card>

      {rows.length > 0 && (
        <Card className="p-3" data-testid="loose-list">
          <div className="text-sm font-medium mb-2">
            Pending Wrap ({rows.length}){filterSku ? ` · ${filterSku}` : ''}
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-stone-100 rounded border">
            {rows.map((r) => (
              <label
                key={r.usn_code}
                className="px-3 py-1.5 flex items-center gap-3 text-sm hover:bg-stone-50 cursor-pointer"
                data-testid={`loose-row-${r.usn_code}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(r.usn_code)}
                  onChange={() => toggle(r.usn_code)}
                  data-testid={`loose-cb-${r.usn_code}`}
                />
                <span className="font-mono flex-1">{r.usn_code}</span>
                <span className="text-xs text-stone-500">
                  {r.sku_code} · {r.design_name || '-'}
                </span>
              </label>
            ))}
          </div>

          <div className="flex gap-2 mt-3">
            <Input
              value={bundleCode}
              onChange={(e) => setBundleCode(e.target.value)}
              placeholder="New bundle code (e.g. BUN######/26-27)"
              className="font-mono uppercase"
              data-testid="loose-bundle-input"
            />
            <Button
              onClick={handleWrap}
              disabled={busy || selected.size === 0 || !bundleCode.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="loose-wrap-btn"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Package className="h-4 w-4 mr-1" /> Wrap ({selected.size})
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function HistoryTab({ apiBase }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`${apiBase}/history?limit=50`);
      setRows(r.data.reversals || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-3" data-testid="history-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">
          Reversal History ({total})
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="history-refresh-btn"
        >
          <RotateCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-stone-500 py-6 text-center">
          No reversals yet.
        </div>
      ) : (
        <div className="divide-y divide-stone-100 rounded border">
          {rows.map((r) => (
            <div
              key={r.reversal_id}
              className="px-3 py-2"
              data-testid={`history-row-${r.reversal_id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-sm">{r.combo_usn}</div>
                  <div className="text-xs text-stone-500">
                    {r.reversed_at} · by {r.reversed_by || '—'}
                  </div>
                </div>
                <Badge variant="outline">
                  {(r.components || []).length} pcs
                </Badge>
              </div>
              <div className="text-xs text-stone-600 mt-1">
                <span className="font-medium">Reason:</span> {r.reason || '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function PerSectionReversalClone() {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/combo-v2-reversal`;

  return (
    <div
      className="min-h-screen bg-[#FFFBF5]"
      data-testid={`per-section-reversal-${slug}`}
    >
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(backRoute)}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Combo Reversal</h1>
              <p className="text-sm text-[#E8DCC8]">
                {sectionName} · undo assembled combos
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <Tabs defaultValue="reverse">
          <TabsList className="grid w-full grid-cols-3" data-testid="reversal-tabs">
            <TabsTrigger value="reverse" data-testid="tab-reverse">
              <ScanLine className="h-4 w-4 mr-1" /> Reverse
            </TabsTrigger>
            <TabsTrigger value="loose" data-testid="tab-loose">
              <Package className="h-4 w-4 mr-1" /> Loose USNs
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="h-4 w-4 mr-1" /> History
            </TabsTrigger>
          </TabsList>
          <TabsContent value="reverse" className="mt-4">
            <ReverseTab apiBase={apiBase} />
          </TabsContent>
          <TabsContent value="loose" className="mt-4">
            <LooseTab apiBase={apiBase} />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <HistoryTab apiBase={apiBase} />
          </TabsContent>
        </Tabs>
        <Alert className="mt-4 bg-amber-50 border-amber-300">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 text-xs">
            Reversal flips the combo to REVERSED, decrements the session, queues
            component USNs for re-wrap, and re-opens the plan if it had been
            COMPLETED. Use sparingly — this is a destructive action.
          </AlertDescription>
        </Alert>
      </main>
    </div>
  );
}
